// Live AI activity feed - renders the caller's most recent Ask DuraLex
// conversations from /api/ai/conversations. Clicking a row whose matter
// has an eId jumps to the matter page.
//
// Backend gap (2026-04-26): the assistant router has no firm-wide AI
// activity feed (no extracts/matches/flags stream). We surface the
// "recent conversations" list as the closest list-shaped data the
// backend exposes today. See `api/ai.ts` for the rationale and the
// swap-in path for when a real /api/ai/firm-stream lands.
//
// No AI marker on these rows: a title is the question someone at the firm
// typed, not words a model wrote. A conversation without a matter eId
// renders but is not clickable: the matter route answers to nothing else.

import { useNavigate } from "react-router-dom";
import { useAIStream } from "../../api/ai";
import type { AiConversationSummary } from "@duralex/shared";
import { formatRelative } from "../../util/time";
import { EntityLink } from "../chrome/EntityLink";
import { SEP } from "../chrome/SepList";

const SKELETON_ROWS = 3;

export function AIStream() {
  const navigate = useNavigate();
  const { items, isLoading, isError, refetch } = useAIStream({ limit: 10 });

  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">AI activity</div>
        <div className="cc-card-sub">{SEP}recent conversations</div>
        <div className="actions">
          <button className="cc-link-btn" onClick={() => refetch()} title="Refresh" type="button">
            Refresh
          </button>
        </div>
      </div>
      <div className="cc-ai-stream">
        {isLoading && <SkeletonRows />}
        {isError && (
          <ErrorState
            message="Couldn't load AI activity."
            onRetry={() => {
              void refetch();
            }}
          />
        )}
        {!isLoading && !isError && items.length === 0 && (
          <EmptyState message="AI assistant has no activity yet - try asking it something." />
        )}
        {!isLoading &&
          !isError &&
          items.map((c) => (
            <AIConversationRow
              key={c.id}
              conversation={c}
              onOpen={(eid) => navigate(`/matter/${eid}`)}
            />
          ))}
      </div>
    </div>
  );
}

interface RowProps {
  conversation: AiConversationSummary;
  onOpen: (eid: string) => void;
}

function AIConversationRow({ conversation, onOpen }: RowProps) {
  const eid = conversation.caseEId ?? null;
  const sourcePill = pickSourcePill(conversation);
  const onClick = () => {
    if (eid) onOpen(eid);
  };
  return (
    <div
      className="cc-ai-stream-item"
      onClick={onClick}
      style={eid ? { cursor: "pointer" } : undefined}
    >
      <div className="cc-ai-stream-text">
        <span className="verb">Asked</span>{" "}
        <span className="target">{conversation.title || "Untitled conversation"}</span>
        {eid && (
          <span className="cc-ai-stream-ref">
            {SEP}
            <EntityLink kind="matter" id={eid} eID={eid} stopPropagation subtle className="eid">
              {eid}
            </EntityLink>
          </span>
        )}
        <div className="cc-ai-stream-meta">
          <span>
            {conversation.messageCount} message{conversation.messageCount === 1 ? "" : "s"}
          </span>
          {sourcePill && <span className="cc-pill muted">{sourcePill}</span>}
        </div>
      </div>
      <div className="cc-ai-stream-time">{formatRelative(conversation.updatedAt)}</div>
    </div>
  );
}

function pickSourcePill(c: AiConversationSummary): string | null {
  if (!c.context) return null;
  if (c.context.firmWide) return "Firm-wide";
  if (c.context.caseId) return "Case";
  if (c.context.clientId) return "Client";
  return null;
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <div key={i} className="cc-ai-stream-item" aria-hidden="true">
          <div className="cc-ai-stream-text">
            <div
              style={{
                height: 10,
                width: "60%",
                background: "var(--line)",
                borderRadius: 4,
                marginBottom: 6,
              }}
            />
            <div
              style={{
                height: 8,
                width: "40%",
                background: "var(--line-2)",
                borderRadius: 4,
              }}
            />
          </div>
          <div className="cc-ai-stream-time">
            <span
              style={{
                display: "inline-block",
                width: 36,
                height: 8,
                background: "var(--line-2)",
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      ))}
    </>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ padding: "16px 18px", display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ color: "var(--muted)", fontSize: 12 }}>{message}</span>
      <button className="cc-link-btn" onClick={onRetry} type="button">
        Retry
      </button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <div style={{ padding: "20px 18px", color: "var(--muted)", fontSize: 12 }}>{message}</div>;
}
